import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Check, Clock, Loader2, Cloud, CloudUpload, Keyboard, Users, BarChart3, Printer, Lock, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ColorLegend } from "@/components/ColorLegend";
import { ColorCell } from "@/components/ColorCell";
import { SystemCell, SystemLegend } from "@/components/SystemCell";
import { InlineEditableCell } from "@/components/InlineEditableCell";
import { PassportCards } from "@/components/PassportCards";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { NotifyClassButton, PublishButton } from "@/components/EmailNotify";
import { TeacherShell } from "@/components/TeacherShell";

const CRITERIA = [
  { key: "respect_consignes", label: "Respect des consignes" },
  { key: "attention", label: "Attention" },
  { key: "comportement", label: "Comportement" },
];

function formatWeek(startISO, endISO) {
  try {
    const s = new Date(startISO);
    const e = new Date(endISO);
    const fmt = (d, opts) => new Intl.DateTimeFormat("fr-FR", opts).format(d);
    return `Semaine du ${fmt(s, { day: "numeric", month: "long" })} au ${fmt(e, {
      day: "numeric", month: "long", year: "numeric",
    })}`;
  } catch {
    return "Semaine en cours";
  }
}

// Légende (composant partagé) — voir ClassPassportTable pour l'usage.

function SaveIndicator({ state }) {
  const saving = state === "saving";
  return (
    <span className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="save-indicator">
      {saving ? <CloudUpload size={14} className="animate-pulse" /> : <Cloud size={14} />}
      {saving ? "Enregistrement…" : "Enregistré"}
    </span>
  );
}

export default function ClassPassportTable() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved");
  const pending = useRef(0);
  const cellRefs = useRef([]); // grille de références [ligne][colonne] pour la navigation clavier
  const rowsCountRef = useRef(0);
  const colsCountRef = useRef(CRITERIA.length);
  const [evalSystem, setEvalSystem] = useState(null);
  const shouldAutofocus = useRef(true);
  const metaRef = useRef(null);
  rowsCountRef.current = rows.length;
  const readOnly = !!meta?.class?.archived;

  const load = useCallback(async (off) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/classes/${id}/week`, { params: { offset: off } });
      setMeta({ class: data.class, week_number: data.week_number, start: data.week_start, end: data.week_end, published: data.published });
      metaRef.current = { class: data.class };
      setEvalSystem(data.eval_system || null);
      setRows(data.rows);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Classe introuvable." : "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(offset); }, [load, offset]);

  // Place le focus sur la première cellule couleur après chargement (usage clavier immédiat).
  useEffect(() => {
    if (!loading && rows.length && shouldAutofocus.current) {
      shouldAutofocus.current = false;
      const el = cellRefs.current?.[0]?.[0];
      if (el && document.activeElement === document.body) el.focus();
    }
  }, [loading, rows]);

  const persist = useCallback(async (passportId, patch) => {
    if (metaRef.current?.class?.archived) {
      toast.info("Classe archivée : lecture seule. Restaurez-la pour la modifier.");
      return;
    }
    pending.current += 1;
    setSaveState("saving");
    try {
      await api.patch(`/passports/${passportId}`, patch);
    } catch {
      toast.error("Échec de l'enregistrement. Réessayez.");
      load(offset);
    } finally {
      pending.current -= 1;
      if (pending.current === 0) setSaveState("saved");
    }
  }, [load, offset]);

  const handleSelectColor = useCallback((passportId, criterion, color) => {
    setRows((prev) =>
      prev.map((r) => (r.passport_id === passportId ? { ...r, [criterion]: color } : r))
    );
    persist(passportId, { [criterion]: color });
  }, [persist]);

  // Système personnalisé : sélection d'un niveau (stockage générique values{}).
  const handleSelectLevel = useCallback((passportId, criterionId, levelId) => {
    setRows((prev) =>
      prev.map((r) => (r.passport_id === passportId
        ? { ...r, values: { ...(r.values || {}), [criterionId]: levelId } }
        : r))
    );
    persist(passportId, { values: { [criterionId]: levelId } });
  }, [persist]);

  const handleSaveText = useCallback((passportId, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.passport_id === passportId ? { ...r, [field]: value } : r))
    );
    persist(passportId, { [field]: value });
  }, [persist]);

  const registerCell = useCallback((row, col, el) => {
    if (!cellRefs.current[row]) cellRefs.current[row] = [];
    if (el) cellRefs.current[row][col] = el; // ne jamais écraser une ref vivante par null
  }, []);

  const focusCell = useCallback((row, col) => {
    const el = cellRefs.current?.[row]?.[col];
    if (el) el.focus();
  }, []);

  // Navigation clavier entre les cellules (menu fermé). Le menu gère ses propres touches.
  const navigateCell = useCallback((dir, row, col) => {
    const cols = colsCountRef.current;
    const lastRow = rowsCountRef.current - 1;
    if (dir === "right") focusCell(row, Math.min(col + 1, cols - 1));
    else if (dir === "left") focusCell(row, Math.max(col - 1, 0));
    else if (dir === "down") focusCell(Math.min(row + 1, lastRow), col);
    else if (dir === "up") focusCell(Math.max(row - 1, 0), col);
  }, [focusCell]);

  const signedCount = rows.filter((r) => r.signed).length;
  const isCustom = evalSystem?.type === "custom";
  const columns = isCustom ? evalSystem.criteria.map((c) => ({ key: c.id, label: c.label })) : CRITERIA;
  colsCountRef.current = columns.length;
  // Desktop / tablette paysage large : tableau ; tablette portrait & smartphone : cartes élèves.
  const isWide = useMediaQuery("(min-width: 1024px)");

  return (
    <TeacherShell active="dashboard" contentClassName="w-full">
      <div className="flex min-h-[100vh] w-full flex-col">
      {/* Barre supérieure (fixe) */}
      <header className="shrink-0 border-b border-slate-100 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-[#0A0A0A]/85">
        <div className="mx-auto max-w-7xl px-3 py-2 sm:px-6 sm:py-3">
          <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate("/enseignant")}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform duration-200 hover:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                data-testid="back-to-dashboard"
                aria-label="Retour au tableau de bord"
              >
                <ArrowLeft size={18} />
              </button>
              <div className="min-w-0">
                <h1 className="truncate text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100 sm:text-lg">
                  {meta?.class?.name || "Classe"}
                  {meta?.class?.level && (
                    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">
                      {meta.class.level}
                    </span>
                  )}
                  {readOnly && (
                    <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="archived-badge">Classe archivée</span>
                  )}
                </h1>
                {meta && (
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400" data-testid="week-label">
                    {formatWeek(meta.start, meta.end)}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate(`/enseignant/classe/${id}/eleves`)}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                data-testid="manage-students-button"
              >
                <Users size={15} /> <span className="hidden sm:inline">Élèves</span>
              </button>
              <button
                onClick={() => navigate(`/enseignant/classe/${id}/tableau-de-bord`)}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                data-testid="dashboard-button"
              >
                <BarChart3 size={15} /> <span className="hidden sm:inline">Tableau de bord</span>
              </button>
              {!readOnly && (
                <button
                  onClick={() => navigate(`/enseignant/classe/${id}/couleurs-collectives${offset ? `?offset=${offset}` : ""}`)}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                  data-testid="bulk-colors-button"
                  aria-label="Couleurs collectives"
                >
                  <Palette size={15} /> <span className="hidden sm:inline">Couleurs collectives</span>
                </button>
              )}
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                data-testid="print-table-button"
              >
                <Printer size={15} /> <span className="hidden sm:inline">Imprimer</span>
              </button>
              {!readOnly && (<>
              <NotifyClassButton classId={id} offset={offset} />
              <PublishButton classId={id} offset={offset} published={meta?.published} onPublished={() => load(offset)} />
              </>
              )}
              <SaveIndicator state={saveState} />
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setOffset((o) => o - 1)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform duration-200 hover:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                  data-testid="prev-week"
                  aria-label="Semaine précédente"
                >
                  <ChevronLeft size={18} />
                </button>
                {offset !== 0 && (
                  <button
                    onClick={() => setOffset(0)}
                    className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400"
                    data-testid="current-week"
                  >
                    Aujourd'hui
                  </button>
                )}
                <button
                  onClick={() => setOffset((o) => o + 1)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform duration-200 hover:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                  data-testid="next-week"
                  aria-label="Semaine suivante"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
          <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 sm:mt-2.5 ${isWide ? "" : "hidden sm:flex"}`}>
            {isCustom ? <SystemLegend system={evalSystem} /> : <ColorLegend />}
            <div className="flex items-center gap-4">
              <span className="hidden items-center gap-1.5 text-xs text-slate-400 lg:flex" data-testid="keyboard-hint">
                <Keyboard size={14} /> Entrée / clic = choisir le niveau · flèches pour naviguer
              </span>
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400" data-testid="signed-summary">
                {signedCount}/{rows.length} passeport{rows.length !== 1 ? "s" : ""} signé{signedCount !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Zone tableau (défilement indépendant, en-tête figé) */}
      <main className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-3 py-3 sm:px-6 sm:py-4">
          <div className="hidden print:mb-4 print:block">
            <h1 className="text-xl font-semibold text-slate-900">
              {meta?.class?.name} {meta?.class?.level ? `(${meta.class.level})` : ""} — Passeports de la semaine
            </h1>
            {meta && <p className="text-sm text-slate-500">{formatWeek(meta.start, meta.end)}</p>}
          </div>
          {readOnly && (
            <div className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 p-4 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 print:hidden" data-testid="archived-readonly-banner">
              <Lock size={16} className="mt-0.5 shrink-0" />
              Cette classe est archivée : consultation uniquement. Restaurez-la depuis « Classes archivées » pour reprendre les évaluations.
            </div>
          )}
          {(() => {
            if (loading) return (
            <div className="flex justify-center py-24" data-testid="table-loading">
              <Loader2 className="animate-spin text-slate-400" size={28} />
            </div>
            );
            if (rows.length === 0) return (
            <div
              className="rounded-2xl border border-dashed border-slate-200 bg-white/50 py-20 text-center text-slate-500 dark:border-white/10"
              data-testid="empty-students"
            >
              Aucun élève dans cette classe pour le moment.
            </div>
            );
            if (!isWide) return (
              <PassportCards
                rows={rows} columns={columns} isCustom={isCustom} evalSystem={evalSystem}
                onSelectColor={handleSelectColor} onSelectLevel={handleSelectLevel} onSaveText={handleSaveText}
              />
            );
            return (
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]">
              <table className="w-full min-w-[1080px] border-separate border-spacing-0" data-testid="passport-table">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    <th className="sticky left-0 top-0 z-30 border-b border-slate-100 bg-white px-4 py-3 dark:border-white/10 dark:bg-[#0f0f0f]">
                      Élève
                    </th>
                    {columns.map((c) => (
                      <th key={c.key} className="sticky top-0 z-20 border-b border-slate-100 bg-white px-3 py-3 text-center dark:border-white/10 dark:bg-[#0f0f0f]">
                        {c.label}
                      </th>
                    ))}
                    <th className="sticky top-0 z-20 border-b border-slate-100 bg-white px-3 py-3 dark:border-white/10 dark:bg-[#0f0f0f]">
                      Notions à revoir
                    </th>
                    <th className="sticky top-0 z-20 border-b border-slate-100 bg-white px-3 py-3 dark:border-white/10 dark:bg-[#0f0f0f]">
                      Commentaire
                    </th>
                    <th className="sticky top-0 z-20 border-b border-slate-100 bg-white px-3 py-3 text-center dark:border-white/10 dark:bg-[#0f0f0f]">
                      Signature parent
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, rowIndex) => {
                    const zebra = rowIndex % 2 === 1;
                    const stickyBg = zebra
                      ? "bg-slate-50/70 dark:bg-white/[0.03]"
                      : "bg-white dark:bg-[#0f0f0f]";
                    return (
                      <tr
                        key={r.passport_id}
                        className={`${zebra ? "bg-slate-50/70 dark:bg-white/[0.02]" : ""} transition-colors hover:bg-sky-50/40 dark:hover:bg-white/[0.04]`}
                        data-testid={`passport-row-${r.student_id}`}
                      >
                        <td className={`sticky left-0 z-10 border-b border-slate-50 px-4 py-3 align-top dark:border-white/5 ${stickyBg}`}>
                          <div className="flex items-center gap-2">
                            <span className="w-5 shrink-0 text-right text-xs tabular-nums text-slate-300">
                              {rowIndex + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => navigate(`/enseignant/eleve/${r.student_id}`)}
                              className="truncate rounded text-left text-sm font-medium text-slate-800 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:text-slate-100"
                              data-testid={`open-student-${r.student_id}`}
                            >
                              {r.student_name}
                            </button>
                          </div>
                        </td>
                        {columns.map((c, colIndex) => (
                          <td key={c.key} className="w-24 border-b border-slate-50 px-2 py-3 align-top dark:border-white/5">
                            {isCustom ? (
                              <SystemCell
                                passportId={r.passport_id}
                                criterion={c.key}
                                value={(r.values || {})[c.key]}
                                levels={evalSystem.levels}
                                scale={evalSystem.scale_type}
                                studentName={r.student_name}
                                rowIndex={rowIndex}
                                colIndex={colIndex}
                                onSelect={handleSelectLevel}
                                onNavigate={navigateCell}
                                registerCell={registerCell}
                              />
                            ) : (
                              <ColorCell
                                passportId={r.passport_id}
                                criterion={c.key}
                                value={r[c.key]}
                                studentName={r.student_name}
                                rowIndex={rowIndex}
                                colIndex={colIndex}
                                onSelect={handleSelectColor}
                                onNavigate={navigateCell}
                                registerCell={registerCell}
                              />
                            )}
                          </td>
                        ))}
                        <td className="w-[28%] min-w-[260px] border-b border-slate-50 px-2 py-2 align-top dark:border-white/5">
                          <InlineEditableCell
                            passportId={r.passport_id}
                            field="notions_a_revoir"
                            value={r.notions_a_revoir}
                            placeholder="Ajouter…"
                            onSave={handleSaveText}
                            label={`Notions à revoir — ${r.student_name}`}
                            testid={`notions-${r.student_id}`}
                          />
                        </td>
                        <td className="w-[28%] min-w-[260px] border-b border-slate-50 px-2 py-2 align-top dark:border-white/5">
                          <InlineEditableCell
                            passportId={r.passport_id}
                            field="commentaire"
                            value={r.commentaire}
                            placeholder="Ajouter…"
                            onSave={handleSaveText}
                            label={`Commentaire — ${r.student_name}`}
                            testid={`commentaire-${r.student_id}`}
                          />
                        </td>
                        <td className="border-b border-slate-50 px-3 py-3 text-center align-top dark:border-white/5">
                          {r.signed ? (
                            <span
                              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                              data-testid={`signature-${r.student_id}`}
                              data-signed="true"
                              title={r.signed_by || ""}
                            >
                              <Check size={14} /> Signé{r.signatures?.length > 1 ? ` (${r.signatures.length})` : ""}
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400"
                              data-testid={`signature-${r.student_id}`}
                              data-signed="false"
                            >
                              <Clock size={14} /> En attente
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>
      </main>
      </div>
    </TeacherShell>
  );
}
